import { t } from "@lingui/macro";
import { BellIcon, TranslateIcon, ShieldCheckIcon, UserCircleIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import type { SettingsTab, SettingsTabId } from "../types/settings";

type SettingsTabsProps = {
  activeTab: SettingsTabId;
  onTabChange: (tab: SettingsTabId) => void;
};

export const SettingsTabs = ({ activeTab, onTabChange }: SettingsTabsProps) => {
  const settingsTabs: SettingsTab[] = [
    {
      id: "profile",
      label: "Mon profil",
      shortLabel: "Profil",
      description: "Informations personnelles",
      icon: UserCircleIcon,
    },
    {
      id: "security",
      label: "Sécurité",
      shortLabel: "Sécurité",
      description: "Mot de passe et protection",
      icon: ShieldCheckIcon,
    },
    {
      id: "appearance",
      label: t`Langue`,
      shortLabel: t`Langue`,
      description: t`Langue de l’application`,
      icon: TranslateIcon,
    },
    {
      id: "notifications",
      label: "Notifications",
      shortLabel: "Alertes",
      description: "Canaux et préférences",
      icon: BellIcon,
    },
  ];

  return (
    <div
      role="tablist"
      aria-label="Rubriques des paramètres"
      className={cn(
        "grid grid-cols-2 gap-1 rounded-2xl",
        "border border-slate-200 bg-white p-1.5",
        "shadow-[0_8px_30px_rgba(15,23,42,0.05)]",
        "lg:grid-cols-4",
      )}
    >
      {settingsTabs.map((tab) => {
        const IconComponent = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            id={`settings-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`settings-panel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => {
              onTabChange(tab.id);
            }}
            className={cn(
              "group relative flex min-h-[64px] items-center gap-3",
              "rounded-xl px-3 py-2.5 text-left",
              "transition-all duration-200",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500",
              "sm:px-4",
              isActive
                ? "bg-indigo-50/80 text-indigo-950"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-900",
            )}
          >
            <span
              className={cn(
                "grid size-9 shrink-0 place-items-center rounded-xl",
                "transition-colors duration-200",
                isActive
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "bg-slate-50 text-slate-500 group-hover:bg-white",
              )}
            >
              <IconComponent aria-hidden size={20} weight={isActive ? "duotone" : "regular"} />
            </span>

            <span className="min-w-0">
              <span className="block truncate text-xs font-bold sm:text-[13px]">
                <span className="sm:hidden">{tab.shortLabel}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </span>

              <span className="mt-0.5 hidden truncate text-[10px] text-slate-500 xl:block">
                {tab.description}
              </span>
            </span>

            <span
              aria-hidden
              className={cn(
                "absolute inset-x-3 bottom-0 h-0.5",
                "origin-center rounded-full bg-emerald-400",
                "transition-transform duration-200",
                isActive ? "scale-x-100" : "scale-x-0",
              )}
            />
          </button>
        );
      })}
    </div>
  );
};
